'use client'

// Next.js
import Image from 'next/image'

// Animation
import { motion } from 'framer-motion'

// design-studio
import {
  Container,
  fadeInUp,
  Lead,
  Section,
  SectionHeader,
  spaceGrotesk,
  staggerContainer,
} from 'design-studio'

const WITHOUT_CL = [
  'Samples likely paths',
  'Edge cases are easy to miss',
  'Humans must manually validate behavior',
]

const WITH_CL = [
  'Decision logic is formalized',
  'Reasoning engine explores behavior',
  'Artifacts expose boundaries and edge cases',
]

export function HowItWorksComparison() {
  return (
    <Section className="relative z-10 py-16 sm:py-24">
      <Container className={`max-w-8xl ${spaceGrotesk.className}`}>
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.2 }}
          className="space-y-12"
        >
          {/* Title + subtitle */}
          <motion.div variants={fadeInUp}>
            <SectionHeader
              pretitle="Mechanism"
              title={
                <>
                  How CodeLogician
                  <br />
                  works
                </>
              }
              description="AI coding agents normally reason statistically. CodeLogician makes them build a mathematical mental model and analyze it."
            />
          </motion.div>

          {/* Two-column comparison */}
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-1 gap-8 md:grid-cols-[23fr_28fr]"
          >
            {/* Left — without CL */}
            <div className="space-y-5">
              <div className="rounded-xl border border-slate-700/50 bg-slate-900/60 p-6 backdrop-blur-sm">
                <p className="mb-4 block text-center text-2xl text-blue-400 text-shadow-sm">
                  LLM Agent only
                </p>
                <Image
                  src="/images/code-logician/codelogician-how-before.png"
                  alt="Without CodeLogician — AI samples likely paths"
                  width={533}
                  height={292}
                  className="h-auto w-full"
                />
              </div>
              <ul className="w-fit space-y-2 md:mx-auto">
                {WITHOUT_CL.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-lg text-slate-400"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right — with CL */}
            <div className="space-y-5">
              <div className="w-full overflow-hidden rounded-xl border border-teal-500/20 bg-slate-900/60 p-6 backdrop-blur-sm">
                <p className="mb-4 block text-center text-2xl text-teal-400 text-shadow-sm">
                  LLM Agent + CodeLogician
                </p>
                <Image
                  src="/images/code-logician/codelogician-how-after.png"
                  alt="With CodeLogician — decision logic formalized"
                  width={533}
                  height={292}
                  className="h-auto w-full"
                />
              </div>
              <ul className="w-fit space-y-2 md:mx-auto">
                {WITH_CL.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-lg text-slate-300"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Closing tagline — section footer */}
          <motion.div
            variants={fadeInUp}
            className="mx-auto max-w-6xl border-l-2 border-teal-400/50 pl-6"
          >
            <Lead size="lg" tone="default" className="text-slate-200">
              CodeLogician turns AI coding from{' '}
              <span className="text-slate-400">&ldquo;looks right&rdquo;</span>{' '}
              into{' '}
              <span className="font-normal text-teal-300">
                &ldquo;behavior understood.&rdquo;
              </span>
            </Lead>
          </motion.div>
        </motion.div>
      </Container>
    </Section>
  )
}
